import { useEffect, useMemo, useState } from 'react'

import { Copy } from 'lucide-react'
import moment from 'moment'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import ChainToken from '@src/components/ChainToken'
import ProcessIcon from '@src/components/icons/Process'
import { ResultCode, StepNames } from '@src/enums'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { useConfigStore } from '@src/zustand_stores'
import { useHistoryStore } from '@src/zustand_stores/history.store'
import { useOrderUpdatesBackground, useOrderUpdatesStore } from '@src/zustand_stores/orderUpdates.store'

const TRANSACTIONS_PER_PAGE = 3

/**
 * Component that displays pending transactions with live time updates
 */
const PendingTransactions = ({ onClose }: { onClose?: () => void }) => {
    const navigate = useNavigate()
    const { pendingOrderIds } = useOrderUpdatesBackground()
    const { stopTrackingOrder, disconnectFromOrder } = useOrderUpdatesStore()
    const getOrder = useHistoryStore(state => state.getOrder)
    const cleanupDefaultTransactions = useHistoryStore(state => state.cleanupDefaultTransactions)
    const availableNetworks = useConfigStore(state => state.availableNetworks)
    const [currentTime, setCurrentTime] = useState(Date.now())
    const [currentPage, setCurrentPage] = useState(1)
    const [isHovering, setIsHovering] = useState(false)

    // Handle navigation to orders page and close drawer if needed
    const handleNavigateToOrders = () => {
        navigate('/orders')
        if (onClose) {
            onClose()
        }
    }

    // Clean up any default/expired transactions when component mounts
    useEffect(() => {
        // First clean up default/expired transactions from history
        const removedOrders = cleanupDefaultTransactions()

        // Then stop tracking and disconnect WebSockets for those orders
        if (removedOrders.length > 0) {
            console.log(`Stopping tracking for ${removedOrders.length} default/expired orders`)

            removedOrders.forEach(({ exchangeId, orderId }) => {
                try {
                    // Stop tracking these orders
                    stopTrackingOrder(exchangeId, orderId)
                    disconnectFromOrder(exchangeId, orderId)
                } catch (error) {
                    console.error(`Error stopping tracking for order:`, { exchangeId, orderId, error })
                }
            })
        }
    }, [])

    // Set up a timer to update the time display every second
    useEffect(() => {
        const timerId = setInterval(() => {
            setCurrentTime(Date.now())
        }, 1000)

        return () => {
            clearInterval(timerId)
        }
    }, [])

    // Get token information for each pending transaction
    const pendingTransactions = useMemo(() => {
        return pendingOrderIds
            .map(({ exchangeId, orderId }) => {
                const orderData = getOrder(exchangeId)

                // Skip transactions that have completed or timed out
                if (
                    orderData.currentStep === StepNames.MATCH_TIMED_OUT ||
                    orderData.result?.status === ResultCode.success
                ) {
                    return null
                }

                return {
                    exchangeId,
                    orderId,
                    data: orderData,
                    startTime: new Date(orderData.createdAt || Date.now()).toISOString(),
                }
            })
            .filter(tx => tx !== null) // Filter out null entries (completed transactions)
            .sort((a, b) => new Date(b!.startTime).getTime() - new Date(a!.startTime).getTime()) // Sort by newest first
    }, [pendingOrderIds, getOrder])

    // Calculate pagination
    const totalPages = Math.ceil(pendingTransactions.length / TRANSACTIONS_PER_PAGE)
    const startIndex = (currentPage - 1) * TRANSACTIONS_PER_PAGE
    const visibleTransactions = pendingTransactions.slice(startIndex, startIndex + TRANSACTIONS_PER_PAGE)

    // If no pending transactions, don't render anything
    if (pendingTransactions.length === 0) {
        return null
    }

    const handleNextPage = (e: React.MouseEvent) => {
        e.stopPropagation() // Prevent event from bubbling up to parent
        setCurrentPage(prev => Math.min(prev + 1, totalPages))
    }

    const handlePrevPage = (e: React.MouseEvent) => {
        e.stopPropagation() // Prevent event from bubbling up to parent
        setCurrentPage(prev => Math.max(prev - 1, 1))
    }

    const formatAmount = (value: bigint | undefined, decimals: number) => {
        if (!value) return '0'
        try {
            return (Number(value) / 10 ** decimals).toLocaleString('en-us', {
                maximumFractionDigits: 8,
            })
        } catch (e) {
            console.error('Error formatting amount:', e)
            return '0'
        }
    }

    const getElapsedTimeString = (startTime: string) => {
        const created = moment(startTime)
        const now = moment(currentTime)
        const duration = moment.duration(now.diff(created))

        // Display precise seconds for recent transactions
        if (duration.asMinutes() < 1) {
            return `${Math.floor(duration.asSeconds())}s`
        } else if (duration.asHours() < 1) {
            return `${Math.floor(duration.asMinutes())}m ${duration.seconds()}s`
        } else {
            return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`
        }
    }

    const getHashOnly = (hash: string) => {
        // Extract just the hash from the URL by taking the last part after /
        const hashOnly = hash.split('/').pop() || hash
        return hashOnly
    }

    // This function has been moved to the component scope

    return (
        <div
            className='mb-4 w-full px-4 transition-all duration-200 ease-in-out'
            onClick={pendingTransactions.length > 0 ? handleNavigateToOrders : undefined}
            style={{ cursor: pendingTransactions.length > 0 ? 'pointer' : 'default' }}
        >
            <div className='mb-2 flex items-center justify-between'>
                <h3
                    className={`text-sm font-medium ${isHovering ? 'text-blue-400' : 'text-gray-400'} transition-colors duration-200`}
                    onMouseEnter={() => pendingTransactions.length > 0 && setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    {pendingTransactions.length} Pending{' '}
                    {pendingTransactions.length === 1 ? 'Transaction' : 'Transactions'}
                </h3>
                {totalPages > 1 && (
                    <div className='flex items-center gap-2 text-sm' onClick={e => e.stopPropagation()}>
                        <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 1}
                            className='rounded bg-[#1D1F23] px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            Prev
                        </button>
                        <span className='text-gray-400'>
                            {currentPage} / {totalPages}
                        </span>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className='rounded bg-[#1D1F23] px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            <div
                className={`w-full space-y-3 rounded-lg ${isHovering ? 'bg-[#2A2D32]' : 'bg-[#1D1F23]'} p-2 transition-colors duration-200`}
            >
                {visibleTransactions.map(tx => {
                    const { sellData, buyData } = tx!.data
                    const timeElapsed = getElapsedTimeString(tx!.startTime)

                    return (
                        <div
                            key={tx!.exchangeId}
                            className='flex w-full cursor-pointer flex-col rounded-lg bg-[#141618] p-2 transition-all duration-200 hover:bg-[#1D1F23] hover:shadow-md'
                            onClick={handleNavigateToOrders}
                        >
                            <div className='mb-2 flex w-full items-center justify-between gap-2 pt-2'>
                                {/* Sell token */}
                                <div className='flex items-center gap-2'>
                                    <ChainToken
                                        networkIcon={
                                            sellData?.chainId ? availableNetworks[sellData.chainId]?.icon : undefined
                                        }
                                        tokenLogo={sellData?.token?.logoURI}
                                        className='h-8 w-8'
                                    />
                                    <div className='flex flex-col'>
                                        <span className='font-medium'>{sellData?.token?.symbol}</span>
                                        <span className='text-sm text-gray-400'>
                                            {formatAmount(sellData?.value, sellData?.token?.decimals || 18)}
                                        </span>
                                    </div>
                                </div>

                                <div className='flex items-center'>
                                    <ProcessIcon />
                                </div>

                                {/* Buy token */}
                                <div className='flex items-center gap-2'>
                                    <ChainToken
                                        networkIcon={
                                            buyData?.chainId ? availableNetworks[buyData.chainId]?.icon : undefined
                                        }
                                        tokenLogo={buyData?.token?.logoURI}
                                        className='h-8 w-8'
                                    />
                                    <div className='flex flex-col'>
                                        <span className='font-medium'>{buyData?.token?.symbol}</span>
                                        <span className='text-sm text-gray-400'>
                                            {formatAmount(buyData?.value, buyData?.token?.decimals || 18)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Origin Chain Transaction */}
                            {tx!.data.result?.sourceTxHash && tx!.data.result.sourceTxHash && (
                                <div className='mt-1 flex w-full items-center justify-between'>
                                    <div className='flex min-w-0 flex-1 items-center space-x-2 overflow-hidden'>
                                        <Copy
                                            className='h-4 w-4 flex-shrink-0 cursor-pointer text-gray-400 hover:text-gray-200'
                                            onClick={e => {
                                                e.stopPropagation() // Prevent navigation when clicking copy
                                                // Make sure the tx hash exists before copying
                                                if (tx?.data?.result?.sourceTxHash) {
                                                    navigator.clipboard.writeText(tx.data.result.sourceTxHash)
                                                    toast.success('Transaction hash copied!', {
                                                        duration: 2000,
                                                    })
                                                }
                                            }}
                                        />
                                        <a
                                            href={`${getBlockExplorerUrlFromChainId(String(sellData?.chainId || ''))}/${getHashOnly(tx!.data.result.sourceTxHash || '')}`}
                                            className='block truncate text-xs text-gray-400 transition-colors hover:text-gray-200'
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            onClick={e => e.stopPropagation()} // Prevent navigation when clicking tx link
                                        >
                                            {getHashOnly(tx!.data.result.sourceTxHash)}
                                        </a>
                                    </div>
                                    <span className='ml-2 flex-shrink-0 text-xs text-[#00FF00]'>{timeElapsed}</span>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default PendingTransactions
